import type { DrilldownView } from "@shared/components/menus/DrilldownMenu"
import { HeaderWithBackArrow } from "@shared/components/menus/DrilldownMenu"
import type { NoteWords } from "@shared/unified-tiptap/extensions/date-annotation-note/date-annotation-note-extension"
import type { BumpOrDelete } from "./BumpOrDeleteView"
import { BumpOrDeleteView } from "./BumpOrDeleteView"
import { LeaveNoteChoiceView } from "./LeaveNoteChoiceView"
import { noteDrafts } from "./note-draft"
import { NoteEditorView } from "./NoteEditorView"

export interface RemoveClassTodayChoice {
  /** What happens to the day's lesson, or null when the day has none to begin with. */
  bumpOrDelete: BumpOrDelete | null
  /** The note explaining the day off, or null to hide the class from the day entirely. */
  note: NoteWords | null
}

const BUMP_OR_DELETE_TITLE = "What should happen to the lesson?"
const LEAVE_NOTE_TITLE = "Do you want to leave a note?"
const NOTE_EDITOR_TITLE = "Leave a note"

/**
 * "Remove this class today" — drilldown steps: what happens to the lesson, whether to leave a
 * note, and then the note itself. The class stays on until the note is created, so backing out
 * of the editor changes nothing.
 */
export function removeClassTodayFlow({
  hasLesson,
  menuTitle,
  courseId,
  color,
  dateString,
  onDone,
}: {
  /** Whether the day has a lesson — adds the "what should happen to it?" step in front. */
  hasLesson: boolean
  /** The root menu's title, named by the first step's back arrow. */
  menuTitle: string
  /** With the day, names the note draft, so an abandoned one can only return to its own class. */
  courseId: string
  /** The course's color, which the note editor is painted on. */
  color: string
  /** The day the note is pinned to. */
  dateString: string
  /** Called with the user's answers; the flow closes the menu afterwards. */
  onDone: (choice: RemoveClassTodayChoice) => void
}): DrilldownView {
  const draftKey = noteDrafts.key(courseId, dateString)

  const noteEditorStep = (bumpOrDelete: BumpOrDelete | null): DrilldownView => ({
    header: ({ back }) => (
      <HeaderWithBackArrow backTo={LEAVE_NOTE_TITLE} onBack={back}>
        {NOTE_EDITOR_TITLE}
      </HeaderWithBackArrow>
    ),
    body: ({ back, close }) => (
      <NoteEditorView
        color={color}
        dateString={dateString}
        draftKey={draftKey}
        onCancel={back}
        onSave={(note) => {
          onDone({ bumpOrDelete, note })
          close()
        }}
      />
    ),
  })

  const leaveNoteStep = (bumpOrDelete: BumpOrDelete | null, backTo: string): DrilldownView => ({
    header: ({ back }) => (
      <HeaderWithBackArrow backTo={backTo} onBack={back}>
        {LEAVE_NOTE_TITLE}
      </HeaderWithBackArrow>
    ),
    body: ({ push, close }) => (
      <LeaveNoteChoiceView
        onLeaveNote={() => push(noteEditorStep(bumpOrDelete))}
        onHideClass={() => {
          // The class is coming off without words: a draft has nothing left to come back to.
          noteDrafts.clear(draftKey)
          onDone({ bumpOrDelete, note: null })
          close()
        }}
      />
    ),
  })

  if (!hasLesson) return leaveNoteStep(null, menuTitle)

  return {
    header: ({ back }) => (
      <HeaderWithBackArrow backTo={menuTitle} onBack={back}>
        {BUMP_OR_DELETE_TITLE}
      </HeaderWithBackArrow>
    ),
    body: ({ push }) => (
      <BumpOrDeleteView onChoose={(fate) => push(leaveNoteStep(fate, BUMP_OR_DELETE_TITLE))} />
    ),
  }
}
